import { useState, useRef, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { MapPin, Loader2 } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

interface CityAutocompleteProps {
  placeholder?: string
  value?: string
  onChange: (city: string) => void
  onBlur?: () => void
  className?: string
  hasError?: boolean
}

interface CityResult {
  city: string
  count: number
}

export const CityAutocomplete = ({
  placeholder,
  value = "",
  onChange,
  onBlur,
  className,
  hasError = false
}: CityAutocompleteProps) => {
  const { t } = useTranslation()
  const [input, setInput] = useState(value)
  const [suggestions, setSuggestions] = useState<CityResult[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const containerRef = useRef<HTMLDivElement>(null)

  // Update internal state when external value changes
  useEffect(() => {
    setInput(value)
  }, [value])

  // Fetch city suggestions from database
  const fetchCitySuggestions = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setSuggestions([])
      return
    }

    setLoading(true)
    try {
      // Get unique cities from active listings that match the search term
      const { data, error } = await supabase
        .from('listings')
        .select('city')
        .eq('is_active', true)
        .ilike('city', `%${searchTerm}%`)
        .order('city')

      if (error) throw error

      // Count occurrences and create unique list
      const cityMap = new Map<string, number>()
      data?.forEach((item) => {
        const city = item.city
        cityMap.set(city, (cityMap.get(city) || 0) + 1)
      })

      const uniqueCities: CityResult[] = Array.from(cityMap.entries())
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count) // Sort by number of listings
        .slice(0, 8) // Limit to 8 suggestions

      setSuggestions(uniqueCities)
      setShowSuggestions(uniqueCities.length > 0)
    } catch (error) {
      console.error('Error fetching city suggestions:', error)
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }

  // Handle input change with debouncing
  const handleInputChange = (newValue: string) => {
    setInput(newValue)
    onChange(newValue.trim())
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (newValue.length >= 3) {
      timeoutRef.current = setTimeout(() => {
        fetchCitySuggestions(newValue)
      }, 300)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (city: string) => {
    setInput(city)
    onChange(city.trim())
    setShowSuggestions(false)
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <Input 
          placeholder={placeholder || t('search.destinationPlaceholder')}
          className={cn(
            "pl-10 h-12 border-0 bg-muted/30 focus:bg-white transition-smooth",
            className
          )}
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={onBlur}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto min-w-[280px]">
          {suggestions.map((result) => (
            <button
              key={result.city}
              type="button"
              className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0 flex items-center justify-between gap-4"
              onClick={() => handleSuggestionSelect(result.city)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium text-sm truncate">{result.city}</span>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                {result.count} {result.count === 1 ? t('common.listing', 'Unterkunft') : t('common.listings', 'Unterkünfte')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
